import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/ui/checkbox";
import { useNotification } from "@/context/NotificationContext";
import { fetchStagesWithPermissions } from "../../api/fetchStagesWithPermissions/fetchStagesWithPermissionsApi";
import {
  StagePermissionCatalogItem,
  StageWithPermissions,
} from "../../api/fetchStagesWithPermissions/types";
import { updateStagePermissions } from "../../api/updateStagePermissions/updateStagePermissionsApi";

type AssignmentMap = Record<string, number[]>;

const toAssignmentMap = (stages: StageWithPermissions[]): AssignmentMap =>
  stages.reduce<AssignmentMap>((map, stage) => {
    map[stage.uuid] = stage.permissions.map((permission) => permission.id);
    return map;
  }, {});

const sameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

const StagePermissionsMatrix: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [stages, setStages] = useState<StageWithPermissions[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<
    StagePermissionCatalogItem[]
  >([]);
  const [assignments, setAssignments] = useState<AssignmentMap>({});
  const [savedAssignments, setSavedAssignments] = useState<AssignmentMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStagesWithPermissions()
      .then((response) => {
        setStages(response.data.stages);
        setPermissionCatalog(response.data.permissions);
        const initial = toAssignmentMap(response.data.stages);
        setAssignments(initial);
        setSavedAssignments(initial);
      })
      .catch((err) => console.error("Failed to load stage permissions:", err))
      .finally(() => setLoading(false));
  }, []);

  const dirtyStageUuids = useMemo(
    () =>
      stages
        .map((stage) => stage.uuid)
        .filter(
          (uuid) =>
            !sameIdSet(assignments[uuid] ?? [], savedAssignments[uuid] ?? []),
        ),
    [stages, assignments, savedAssignments],
  );

  const isDirty = dirtyStageUuids.length > 0;

  const toggleAssignment = useCallback(
    (stageUuid: string, permissionId: number) => {
      setAssignments((prev) => {
        const current = prev[stageUuid] ?? [];
        const next = current.includes(permissionId)
          ? current.filter((id) => id !== permissionId)
          : [...current, permissionId];
        return { ...prev, [stageUuid]: next };
      });
    },
    [],
  );

  const setAllForStage = useCallback(
    (stageUuid: string, selectAll: boolean) => {
      setAssignments((prev) => ({
        ...prev,
        [stageUuid]: selectAll
          ? permissionCatalog.map((permission) => permission.id)
          : [],
      }));
    },
    [permissionCatalog],
  );

  const stageSelectionState = (
    stageUuid: string,
  ): boolean | "indeterminate" => {
    const count = (assignments[stageUuid] ?? []).length;
    if (count === 0) return false;
    if (count === permissionCatalog.length) return true;
    return "indeterminate";
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      for (const stageUuid of dirtyStageUuids) {
        await updateStagePermissions(stageUuid, {
          permissionIds: assignments[stageUuid] ?? [],
        });
      }
      setSavedAssignments(assignments);
      showNotification(
        "Stage permissions saved",
        "Members pick up stage permission changes on their next page load.",
        "success",
      );
    } catch {
      // Error handled by axios interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainPageWrapper
      actions={
        <div className="flex items-center gap-3">
          {isDirty && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Unsaved changes in {dirtyStageUuids.length}{" "}
              {dirtyStageUuids.length === 1 ? "stage" : "stages"}
            </span>
          )}
          <Button
            onClick={() => navigate("/admin/stages")}
            type="button"
            variant="outline"
          >
            Back to Stages
          </Button>
          <Button disabled={!isDirty || saving} onClick={handleSave}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
      loading={loading}
      subtitle="Permissions granted to every member of a company in each stage."
      title="Stage Permissions"
    >
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
        <table className="w-full min-w-[640px] bg-white dark:bg-slate-800 text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700">
              <th className="px-4 py-3 text-left font-normal text-gray-500 dark:text-gray-400">
                Permission
              </th>
              {stages.map((stage) => (
                <th
                  className="px-4 py-3 text-center font-normal text-gray-900 dark:text-white"
                  key={stage.uuid}
                >
                  <span className="inline-flex flex-col items-center gap-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      {stage.name}
                      {dirtyStageUuids.includes(stage.uuid) && (
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-amber-500"
                          title="Unsaved changes"
                        />
                      )}
                    </span>
                    <Checkbox
                      aria-label={`Select all permissions for ${stage.name}`}
                      checked={stageSelectionState(stage.uuid)}
                      disabled={saving || permissionCatalog.length === 0}
                      onCheckedChange={() =>
                        setAllForStage(
                          stage.uuid,
                          stageSelectionState(stage.uuid) !== true,
                        )
                      }
                      size="sm"
                      title="Select all"
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {permissionCatalog.map((permission) => (
              <tr key={permission.id}>
                <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">
                  {permission.label}
                </td>
                {stages.map((stage) => (
                  <td className="px-4 py-2.5 text-center" key={stage.uuid}>
                    <Checkbox
                      aria-label={`${permission.label} for ${stage.name}`}
                      checked={(assignments[stage.uuid] ?? []).includes(
                        permission.id,
                      )}
                      disabled={saving}
                      onCheckedChange={() =>
                        toggleAssignment(stage.uuid, permission.id)
                      }
                      size="sm"
                    />
                  </td>
                ))}
              </tr>
            ))}
            {!loading && permissionCatalog.length === 0 && (
              <tr>
                <td
                  className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                  colSpan={stages.length + 1}
                >
                  No permissions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MainPageWrapper>
  );
};

export default StagePermissionsMatrix;
